/**
 * FRONTEND PERMISSION CONSTANTS
 * Mirror of backend permission definitions for consistent UI
 */

export const PERMISSION_PAGES = {
  devices: {
    label: 'Modules',
    description: 'IoT device management',
    actions: {
      view: { label: 'View Devices', category: 'CRUD Operations' },
      create: { label: 'Create Device', category: 'CRUD Operations' },
      edit: { label: 'Edit Device', category: 'CRUD Operations' },
      delete: { label: 'Delete Device', category: 'CRUD Operations' },
      sync: { label: 'Sync Devices', category: 'CRUD Operations' },
      restart: { label: 'Restart Device', category: 'CRUD Operations' }
    }
  },

  rooms: {
    label: 'Rooms',
    description: 'Room management and operations',
    actions: {
      view: { label: 'View Rooms', category: 'Manage Rooms' },
      create: { label: 'Create Room', category: 'Manage Rooms' },
      edit: { label: 'Edit Room', category: 'Manage Rooms' },
      delete: { label: 'Delete Room', category: 'Manage Rooms' },
      checkin: { label: 'Check-in', category: 'Room Operations' },
      checkout: { label: 'Check-out', category: 'Room Operations' },
      viewPassword: { label: 'View WiFi Password', category: 'WiFi Access' },
      editGuest: { label: 'Update Guest Information', category: 'Guest Management' },
      viewGuestDetails: { label: 'View Guest Details', category: 'Guest Management' },
      exportData: { label: 'Export Guest Details', category: 'Data Export' }
    }
  },

  vouchers: {
    label: 'Vouchers/Coupons',
    description: 'WiFi voucher management',
    actions: {
      view: { label: 'View Vouchers', category: 'View Access' },
      create: { label: 'Create Voucher', category: 'Redemption Types' },
      edit: { label: 'Edit Voucher', category: 'Redemption Types' },
      delete: { label: 'Delete Voucher', category: 'Redemption Types' },
      generate: { label: 'Generate Vouchers', category: 'Redemption Types' },
      redeem: { label: 'Redeem Voucher', category: 'Redemption Types' },
      import: { label: 'Import Vouchers', category: 'Import' },
      addonManagement: { label: 'Manage Add-ons', category: 'Add-on Management' }
    }
  },

  users: {
    label: 'User Management',
    description: 'System user administration',
    actions: {
      view: { label: 'View Users', category: 'User Management' },
      create: { label: 'Create User', category: 'User Management' },
      edit: { label: 'Edit User', category: 'User Management' },
      delete: { label: 'Delete User', category: 'User Management' },
      disable: { label: 'Disable/Enable User', category: 'User Management' },
      assignRoles: { label: 'Assign Roles', category: 'User Management' }
    }
  },

  settings: {
    label: 'Settings',
    description: 'System configuration',
    actions: {
      view: { label: 'View Settings', category: 'View Access' },
      edit: { label: 'Edit Settings', category: 'View Access' },
      toggleIntegrations: { label: 'Toggle Integrations', category: 'Integration Controls' },
      configureValidation: { label: 'Configure Validation Rules', category: 'Validation Rules' }
    }
  },

  logs: {
    label: 'Logs & Audit',
    description: 'System audit and logs',
    actions: {
      view: { label: 'View Logs', category: 'View Access' },
      export: { label: 'Export Logs', category: 'Export' },
      delete: { label: 'Delete Logs', category: 'Export' }
    }
  },

  branches: {
    label: 'Branch Management',
    description: 'Organization branch management',
    actions: {
      view: { label: 'View Branches', category: 'CRUD Operations' },
      create: { label: 'Create Branch', category: 'CRUD Operations' },
      edit: { label: 'Edit Branch', category: 'CRUD Operations' },
      delete: { label: 'Delete Branch', category: 'CRUD Operations' }
    }
  },

  reports: {
    label: 'Reports',
    description: 'System reporting',
    actions: {
      view: { label: 'View Reports', category: 'View Access' },
      generate: { label: 'Generate Reports', category: 'Generate' },
      export: { label: 'Export Reports', category: 'Export' },
      schedule: { label: 'Schedule Reports', category: 'Export' }
    }
  }
};

export const DEFAULT_ROLE_PERMISSIONS = {
  super_admin: {
    devices: { enabled: true, actions: Object.keys(PERMISSION_PAGES.devices.actions).map(a => ({ action: a, allowed: true })) },
    rooms: { enabled: true, actions: Object.keys(PERMISSION_PAGES.rooms.actions).map(a => ({ action: a, allowed: true })) },
    vouchers: { enabled: true, actions: Object.keys(PERMISSION_PAGES.vouchers.actions).map(a => ({ action: a, allowed: true })) },
    users: { enabled: true, actions: Object.keys(PERMISSION_PAGES.users.actions).map(a => ({ action: a, allowed: true })) },
    settings: { enabled: true, actions: Object.keys(PERMISSION_PAGES.settings.actions).map(a => ({ action: a, allowed: true })) },
    logs: { enabled: true, actions: Object.keys(PERMISSION_PAGES.logs.actions).map(a => ({ action: a, allowed: true })) },
    branches: { enabled: true, actions: Object.keys(PERMISSION_PAGES.branches.actions).map(a => ({ action: a, allowed: true })) },
    reports: { enabled: true, actions: Object.keys(PERMISSION_PAGES.reports.actions).map(a => ({ action: a, allowed: true })) }
  },

  admin: {
    devices: { enabled: true, actions: Object.keys(PERMISSION_PAGES.devices.actions).map(a => ({ action: a, allowed: true })) },
    rooms: { enabled: true, actions: Object.keys(PERMISSION_PAGES.rooms.actions).map(a => ({ action: a, allowed: true })) },
    vouchers: { enabled: true, actions: Object.keys(PERMISSION_PAGES.vouchers.actions).map(a => ({ action: a, allowed: true })) },
    users: { enabled: true, actions: Object.keys(PERMISSION_PAGES.users.actions).map(a => ({ action: a, allowed: true })) },
    settings: { enabled: true, actions: Object.keys(PERMISSION_PAGES.settings.actions).map(a => ({ action: a, allowed: true })) },
    logs: { enabled: false, actions: [] },
    branches: { enabled: false, actions: [] },
    reports: { enabled: true, actions: Object.keys(PERMISSION_PAGES.reports.actions).map(a => ({ action: a, allowed: true })) }
  },

  branch_manager: {
    devices: { enabled: true, actions: [
      { action: 'view', allowed: true },
      { action: 'create', allowed: true },
      { action: 'edit', allowed: true },
      { action: 'delete', allowed: false },
      { action: 'sync', allowed: true },
      { action: 'restart', allowed: true }
    ]},
    rooms: { enabled: true, actions: Object.keys(PERMISSION_PAGES.rooms.actions).map(a => ({ action: a, allowed: true })) },
    vouchers: { enabled: true, actions: Object.keys(PERMISSION_PAGES.vouchers.actions).map(a => ({ action: a, allowed: true })) },
    users: { enabled: false, actions: [] },
    settings: { enabled: false, actions: [] },
    logs: { enabled: true, actions: [
      { action: 'view', allowed: true },
      { action: 'export', allowed: false },
      { action: 'delete', allowed: false }
    ]},
    branches: { enabled: false, actions: [] },
    reports: { enabled: true, actions: [
      { action: 'view', allowed: true },
      { action: 'generate', allowed: true },
      { action: 'export', allowed: true },
      { action: 'schedule', allowed: false }
    ]}
  },

  branchoperator: {
    devices: { enabled: false, actions: [] },
    rooms: { enabled: true, actions: [
      { action: 'view', allowed: true },
      { action: 'create', allowed: false },
      { action: 'edit', allowed: true },
      { action: 'delete', allowed: false },
      { action: 'checkin', allowed: true },
      { action: 'checkout', allowed: true },
      { action: 'viewPassword', allowed: true },
      { action: 'editGuest', allowed: true },
      { action: 'exportData', allowed: false }
    ]},
    vouchers: { enabled: true, actions: [
      { action: 'view', allowed: true },
      { action: 'create', allowed: false },
      { action: 'edit', allowed: false },
      { action: 'delete', allowed: false },
      { action: 'generate', allowed: true },
      { action: 'redeem', allowed: true },
      { action: 'import', allowed: false },
      { action: 'addonManagement', allowed: false }
    ]},
    users: { enabled: false, actions: [] },
    settings: { enabled: false, actions: [] },
    logs: { enabled: false, actions: [] },
    branches: { enabled: false, actions: [] },
    reports: { enabled: false, actions: [] }
  }
};

export const BRANCH_ACCESS_RULES = {
  super_admin: {
    description: 'Full access to all branches across the system',
    canViewAll: true,
    canManageAll: true
  },
  admin: {
    description: 'Access limited to assigned branches only',
    canViewAll: false,
    canManageAll: false
  },
  branch_manager: {
    description: 'Access limited to primary branch only',
    canViewAll: false,
    canManageAll: false
  },
  branchoperator: {
    description: 'Access limited to primary branch only',
    canViewAll: false,
    canManageAll: false
  }
};
